"use client";

import { Trash2 } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";

const DELETE_ACTION_WIDTH = 80;
const DELETE_HINT_WIDTH = 20;
const DELETE_COMMIT_RATIO = 0.7;
const DRAG_THRESHOLD = 6;
const INTERACTIVE_CONTROL_SELECTOR =
  'a, button, input, select, textarea, [contenteditable]:not([contenteditable="false"])';

interface DragState {
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  readonly startReveal: number;
  moved: boolean;
}

interface StopwatchSwipeToDeleteProps {
  readonly children: ReactNode;
  readonly deleteLabel: string;
  readonly deleteText: string;
  readonly isRtl: boolean;
  readonly showHint: boolean;
  readonly storyTarget?: "timing";
  readonly onDelete: () => void;
}

export function StopwatchSwipeToDelete({
  children,
  deleteLabel,
  deleteText,
  isRtl,
  showHint,
  storyTarget,
  onDelete,
}: StopwatchSwipeToDeleteProps) {
  const [reveal, setReveal] = useState(0);
  const [surfaceWidth, setSurfaceWidth] = useState(DELETE_ACTION_WIDTH);
  const [dragging, setDragging] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const revealRef = useRef(0);
  const deleteReadyRef = useRef(false);
  const deletingRef = useRef(false);
  const deleteDispatchedRef = useRef(false);
  const dragRef = useRef<DragState | null>(null);
  const suppressClickRef = useRef(false);
  const hintingRef = useRef(false);
  const hintTapRef = useRef(false);

  const updateReveal = useCallback((nextReveal: number) => {
    const measuredSurfaceWidth =
      surfaceRef.current?.clientWidth || DELETE_ACTION_WIDTH;
    const boundedReveal = Math.min(
      measuredSurfaceWidth,
      Math.max(0, nextReveal),
    );
    const nextDeleteReady =
      measuredSurfaceWidth > 0 &&
      boundedReveal >= measuredSurfaceWidth * DELETE_COMMIT_RATIO;
    revealRef.current = boundedReveal;
    deleteReadyRef.current = nextDeleteReady;
    setSurfaceWidth(measuredSurfaceWidth);
    setReveal(boundedReveal);
  }, []);

  const dispatchDelete = useCallback(() => {
    if (deleteDispatchedRef.current) {
      return;
    }
    deleteDispatchedRef.current = true;
    onDelete();
  }, [onDelete]);

  const beginDelete = useCallback(() => {
    if (deletingRef.current) {
      return;
    }
    deletingRef.current = true;
    dragRef.current = null;
    setDragging(false);

    if (
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
    ) {
      dispatchDelete();
      return;
    }

    setDeleting(true);
    updateReveal(surfaceRef.current?.clientWidth || DELETE_ACTION_WIDTH);
  }, [dispatchDelete, updateReveal]);

  useEffect(() => {
    const reducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    );
    if (!showHint || reducedMotion?.matches) {
      return;
    }

    const revealTimeout = window.setTimeout(() => {
      hintingRef.current = true;
      updateReveal(DELETE_HINT_WIDTH);
    }, 0);
    const hintTimeout = window.setTimeout(() => {
      hintingRef.current = false;
      updateReveal(0);
    }, 1400);
    return () => {
      hintingRef.current = false;
      hintTapRef.current = false;
      window.clearTimeout(revealTimeout);
      window.clearTimeout(hintTimeout);
    };
  }, [showHint, updateReveal]);

  const finishDrag = () => {
    const drag = dragRef.current;
    if (drag === null) {
      return;
    }
    if (hintTapRef.current && !drag.moved) {
      suppressClickRef.current = false;
      dragRef.current = null;
      setDragging(false);
      return;
    }
    suppressClickRef.current = drag.moved;
    dragRef.current = null;
    setDragging(false);
    if (deleteReadyRef.current) {
      beginDelete();
      return;
    }
    updateReveal(0);
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }
    if (deletingRef.current) {
      return;
    }
    if (
      event.target instanceof Element &&
      event.target.closest(INTERACTIVE_CONTROL_SELECTOR) !== null
    ) {
      return;
    }
    if (hintingRef.current) {
      hintTapRef.current = true;
    }
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startReveal: revealRef.current,
      moved: false,
    };
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (drag === null || drag.pointerId !== event.pointerId) {
      return;
    }
    const horizontalDelta = isRtl
      ? event.clientX - drag.startX
      : drag.startX - event.clientX;
    const verticalDelta = Math.abs(event.clientY - drag.startY);
    if (!drag.moved) {
      if (verticalDelta > Math.abs(horizontalDelta)) {
        return;
      }
      if (Math.abs(horizontalDelta) < DRAG_THRESHOLD) {
        return;
      }
      hintTapRef.current = false;
      hintingRef.current = false;
      drag.moved = true;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setDragging(true);
    }
    event.preventDefault();
    updateReveal(drag.startReveal + horizontalDelta);
  };

  const handleClickCapture = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (deletingRef.current) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (hintTapRef.current) {
      hintTapRef.current = false;
      hintingRef.current = false;
      updateReveal(0);
      return;
    }
    if (hintingRef.current) {
      hintingRef.current = false;
      updateReveal(0);
      return;
    }
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (revealRef.current > 0) {
      event.preventDefault();
      event.stopPropagation();
      updateReveal(0);
    }
  };

  const handlePointerCancel = () => {
    if (dragRef.current === null) {
      return;
    }
    suppressClickRef.current = dragRef.current.moved;
    dragRef.current = null;
    setDragging(false);
    updateReveal(0);
  };

  const signedReveal = isRtl ? reveal : -reveal;
  const actionOffset = Math.max(0, (surfaceWidth - reveal) / 2);
  const signedActionOffset = isRtl ? -actionOffset : actionOffset;
  const actionProgress = Math.min(
    1,
    reveal / (surfaceWidth * DELETE_COMMIT_RATIO),
  );
  const actionScale = 1 + actionProgress * 0.15;
  const actionVisible = reveal > 0 || deleting;

  return (
    <div
      className="relative overflow-hidden rounded-lg"
      data-stopwatch-story-target={storyTarget}
    >
      <div
        className={`absolute inset-0 flex ${
          actionVisible
            ? "bg-danger text-danger-ink"
            : "bg-surface text-transparent"
        }`}
      >
        <button
          type="button"
          className="flex min-h-11 w-full items-center justify-center px-5 text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus"
          aria-label={deleteLabel}
          onFocus={() => updateReveal(DELETE_ACTION_WIDTH)}
          onClick={beginDelete}
        >
          <span
            className={`inline-flex items-center gap-2 transition-transform duration-[var(--motion-duration-fast)] ease-[var(--motion-easing-standard)] motion-reduce:transition-none ${
              dragging ? "transition-none" : ""
            }`}
            style={{
              transform: `translate3d(${signedActionOffset}px, 0, 0) scale(${actionScale.toFixed(3)})`,
            }}
          >
            <Trash2 className="size-5" aria-hidden="true" />
            <span>{deleteText}</span>
          </span>
        </button>
      </div>
      <div
        ref={surfaceRef}
        className={`relative touch-pan-y bg-surface transition-[transform,opacity] duration-[var(--motion-duration-emphasized)] ease-[var(--motion-easing-standard)] motion-reduce:transition-none ${
          dragging ? "transition-none" : deleting ? "opacity-0" : "opacity-100"
        }`}
        style={{ transform: `translate3d(${signedReveal}px, 0, 0)` }}
        onClickCapture={handleClickCapture}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={handlePointerCancel}
        onTransitionEnd={(event) => {
          if (
            deleting &&
            event.currentTarget === event.target &&
            event.propertyName === "opacity"
          ) {
            dispatchDelete();
          }
        }}
      >
        {children}
      </div>
    </div>
  );
}
